
  import { defineComponent, inject, PropType, Ref } from 'vue';

  import { AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY } from '@/common/components/redesigned/Table/constants/exportInjection';
  import constants from '@/common/constants';
  import Utils from '@/common/utils';
  import { downloadPDF } from '@/common/utils/fileExport';
  import { buildAggregatedPagePdfTablesCloneFormatter } from '@/common/utils/pdfSplitWideTablesForClone';

  import { EXCEL_ERROR_MESSAGE, EXCEL_ERROR_TITLE } from '@/modules/ved/constants/error';

  import VedExportButtons from '../VedExportButtons.vue';
  import { AGGREGATED_EXPORT_PROGRESS_KEY } from './exportInjection';
  import { TExportConfig } from './types';

  export default defineComponent({
    name: 'VedAggregatedExportButtons',
    components: {
      VedExportButtons,
    },
    props: {
      config: {
        type: Object as PropType<TExportConfig>,
        required: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      const isTableVirtualizationDisabledForPdfExport = inject(AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY);
      const isExportProgress = inject<Ref<boolean>>(AGGREGATED_EXPORT_PROGRESS_KEY);

      return { isTableVirtualizationDisabledForPdfExport, isExportProgress };
    },
    computed: {
      getExcelConfig() {
        return this.config.excel;
      },
    },
    methods: {
      async printPage() {
        if (this.isExportProgress) return;
        this.isExportProgress = true;

        try {
          this.isTableVirtualizationDisabledForPdfExport = true;
          await this.$nextTick();
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });
          const pdfPromise = downloadPDF({
            clonedElementClassName: 'aggregated-page__export-pdf',
            fileName: this.config.pdfFileName,
            pagebreakMode: 'row-friendly',
            clonedNodeFormatter: buildAggregatedPagePdfTablesCloneFormatter(),
            chunkedCanvasRender: {
              enabled: true,
              maxCanvasHeightPx: 7000,
            },
          });

          this.isTableVirtualizationDisabledForPdfExport = false;
          await pdfPromise;
        } finally {
          this.isExportProgress = false;
        }
      },
      async exportExcel() {
        if (this.isExportProgress || !this.getExcelConfig) return;
        this.isExportProgress = true;

        try {
          await Utils.downloadFile(
            this.getExcelConfig.url,
            this.config.excelFileName,
            this.getExcelConfig.params,
            true,
            'post',
          );
        } catch {
          constants.alert.fire(EXCEL_ERROR_TITLE, EXCEL_ERROR_MESSAGE, 'error');
        } finally {
          this.isExportProgress = false;
        }
      },
    },
  });
